/**
 * Client TMDB mutualisé.
 *
 * Remplace les appels `fetch` dispersés (CastSection, CastSectionWithFallback…)
 * qui refaisaient la même requête à chaque montage de composant.
 *
 * Apports :
 *  - clé API centralisée (surchargeable par REACT_APP_TMDB_KEY) ;
 *  - cache mémoire + sessionStorage : une fiche n'est chargée qu'une fois par session ;
 *  - déduplication des requêtes concurrentes (deux composants qui demandent
 *    le même casting au même instant ne déclenchent qu'un seul appel réseau) ;
 *  - annulation par timeout, pour ne jamais laisser une vue bloquée en chargement.
 */

const KEY = process.env.REACT_APP_TMDB_KEY || '50515256085bfa6be9f3be3379422d2a';
const BASE = 'https://api.themoviedb.org/3';
const LANG = 'fr-FR';
const IMG = 'https://image.tmdb.org/t/p';

const TIMEOUT_MS = 9000;
const TTL_MS = 24 * 60 * 60 * 1000; // les crédits d'un film ne bougent pas
const STORE_PREFIX = 'tmdb:';

/** Cache mémoire : { [key]: value } */
const memory = new Map();
/** Requêtes en vol : { [key]: Promise } — évite les appels dupliqués */
const inflight = new Map();

/* ── Images ─────────────────────────────────────────────────── */
export const posterUrl = (path, size = 'w342') => (path ? `${IMG}/${size}${path}` : null);
export const profileUrl = (path, size = 'w185') => (path ? `${IMG}/${size}${path}` : null);
export const backdropUrl = (path, size = 'w1280') => (path ? `${IMG}/${size}${path}` : null);

/* ── Persistance légère ─────────────────────────────────────── */
function readStore(key) {
  try {
    const raw = sessionStorage.getItem(STORE_PREFIX + key);
    if (!raw) return undefined;
    const { t, v } = JSON.parse(raw);
    if (Date.now() - t > TTL_MS) {
      sessionStorage.removeItem(STORE_PREFIX + key);
      return undefined;
    }
    return v;
  } catch {
    return undefined;
  }
}

function writeStore(key, value) {
  try {
    sessionStorage.setItem(STORE_PREFIX + key, JSON.stringify({ t: Date.now(), v: value }));
  } catch {
    // quota dépassé ou mode privé : le cache mémoire suffit
  }
}

/* ── Appel générique ────────────────────────────────────────── */
async function get(path, params = {}, cacheKey = null) {
  const key = cacheKey || `${path}?${new URLSearchParams(params)}`;

  if (memory.has(key)) return memory.get(key);

  const stored = readStore(key);
  if (stored !== undefined) {
    memory.set(key, stored);
    return stored;
  }

  if (inflight.has(key)) return inflight.get(key);

  const url = `${BASE}${path}?${new URLSearchParams({ api_key: KEY, language: LANG, ...params })}`;

  const task = (async () => {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
    try {
      const res = await fetch(url, { signal: ctrl.signal });
      if (!res.ok) throw new Error(`TMDB ${res.status}`);
      const data = await res.json();
      memory.set(key, data);
      writeStore(key, data);
      return data;
    } finally {
      clearTimeout(timer);
      inflight.delete(key);
    }
  })();

  inflight.set(key, task);
  return task;
}

/* ── Normalisation ──────────────────────────────────────────── */

/** Une personne au format interne, quelle que soit la source TMDB. */
function toPerson(raw) {
  return {
    tmdbId: raw.id,
    name: raw.name,
    photo: profileUrl(raw.profile_path),
    photoLarge: profileUrl(raw.profile_path, 'h632'),
    character: raw.character || null,
    job: raw.job || null,
    department: raw.known_for_department || null,
    popularity: raw.popularity || 0,
  };
}

/** Un titre TMDB (film ou série) au format interne. */
function toTitle(raw) {
  const isTv = raw.media_type === 'tv' || Boolean(raw.first_air_date) || Boolean(raw.name && !raw.title);
  const date = isTv ? raw.first_air_date : raw.release_date;
  return {
    tmdbId: raw.id,
    title: isTv ? raw.name : raw.title,
    kind: isTv ? 'serie' : 'film',
    poster: posterUrl(raw.poster_path),
    backdrop: backdropUrl(raw.backdrop_path),
    year: date ? Number(String(date).slice(0, 4)) : null,
    date: date || null,
    rating: raw.vote_average || 0,
    voteCount: raw.vote_count || 0,
    popularity: raw.popularity || 0,
    overview: raw.overview || '',
    character: raw.character || null,
    job: raw.job || null,
  };
}

/* ── API publique ───────────────────────────────────────────── */

/**
 * Distribution + réalisation d'un titre.
 * @param {number|string} tmdbId
 * @param {'film'|'serie'} type
 */
export async function getCredits(tmdbId, type = 'film') {
  if (!tmdbId) return { cast: [], crew: [], directors: [] };
  const media = type === 'serie' || type === 'tv' ? 'tv' : 'movie';
  try {
    const data = await get(`/${media}/${tmdbId}/credits`);
    const cast = (data.cast || []).map(toPerson);
    const crew = (data.crew || []).map(toPerson);
    return {
      cast,
      crew,
      directors: crew.filter((c) => c.job === 'Director' || c.job === 'Series Director'),
    };
  } catch {
    return { cast: [], crew: [], directors: [] };
  }
}

/** Fiche détaillée d'une personne. */
export async function getPerson(personId) {
  if (!personId) return null;
  try {
    const raw = await get(`/person/${personId}`);
    return {
      ...toPerson(raw),
      biography: raw.biography || '',
      birthday: raw.birthday || null,
      deathday: raw.deathday || null,
      placeOfBirth: raw.place_of_birth || null,
      homepage: raw.homepage || null,
      imdbId: raw.imdb_id || null,
    };
  } catch {
    return null;
  }
}

/**
 * Filmographie complète d'une personne (films + séries), dédoublonnée
 * et triée du plus récent au plus ancien.
 */
export async function getPersonCredits(personId) {
  if (!personId) return { acting: [], directing: [] };
  try {
    const data = await get(`/person/${personId}/combined_credits`);

    const seen = new Set();
    const acting = [];
    for (const raw of data.cast || []) {
      if (seen.has(raw.id)) continue;
      seen.add(raw.id);
      acting.push(toTitle(raw));
    }

    const directing = (data.crew || [])
      .filter((c) => c.job === 'Director')
      .map(toTitle);

    const byRecency = (a, b) => (b.year || 0) - (a.year || 0) || b.popularity - a.popularity;
    return { acting: acting.sort(byRecency), directing: directing.sort(byRecency) };
  } catch {
    return { acting: [], directing: [] };
  }
}

/** Recherche de personnes par nom. */
export async function searchPeople(query) {
  const q = (query || '').trim();
  if (q.length < 2) return [];
  try {
    const data = await get('/search/person', { query: q, include_adult: 'false' });
    return (data.results || [])
      .map(toPerson)
      .sort((a, b) => b.popularity - a.popularity);
  } catch {
    return [];
  }
}

/**
 * Retrouve l'identifiant TMDB d'une personne à partir de son seul nom.
 * Le catalogue local ne stocke que des chaînes de caractères : c'est ce
 * pont qui permet d'ouvrir une fiche acteur depuis un nom brut.
 */
export async function resolvePersonByName(name) {
  const clean = (name || '').trim();
  if (!clean) return null;
  const people = await searchPeople(clean);
  if (!people.length) return null;
  const exact = people.find((p) => p.name.toLowerCase() === clean.toLowerCase());
  return exact || people[0];
}

/** Recherche de titres (utilisée par le formulaire de demande de film). */
export async function searchTitles(query, type = 'movie') {
  const q = (query || '').trim();
  if (q.length < 2) return [];
  try {
    const data = await get(`/search/${type === 'tv' ? 'tv' : 'movie'}`, { query: q, include_adult: 'false' });
    return (data.results || []).map(toTitle);
  } catch {
    return [];
  }
}

const tmdb = {
  getCredits,
  getPerson,
  getPersonCredits,
  searchPeople,
  resolvePersonByName,
  searchTitles,
  posterUrl,
  profileUrl,
  backdropUrl,
};

export default tmdb;
